import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, Type, Loader2, Eraser } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// Signature draw pad. Simple mouse+touch capable canvas.
function SignaturePad({ onCommit }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const hasStrokes = useRef(false);

  const start = (e) => {
    drawing.current = true;
    hasStrokes.current = true;
    last.current = pointOf(e, canvasRef.current);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pointOf(e, canvasRef.current);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#1A1F2C";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    drawing.current = false;
    if (hasStrokes.current) onCommit(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasStrokes.current = false;
    onCommit("");
  };

  return (
    <div>
      <div className="rounded-md border-2 border-dashed border-[#1A1F2C]/25 bg-[#FDFBF7]">
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: "100%", height: 180, touchAction: "none", cursor: "crosshair" }}
          data-testid="sign-canvas"
        />
      </div>
      <div className="flex items-center justify-between text-xs text-[#5C6B7A] mt-1">
        <span>Draw with your finger or mouse.</span>
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 hover:text-[#C85A32]" data-testid="sign-clear">
          <Eraser size={12} /> Clear
        </button>
      </div>
    </div>
  );
}

function pointOf(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const t = e.touches ? e.touches[0] : e;
  return {
    x: ((t.clientX - rect.left) / rect.width) * canvas.width,
    y: ((t.clientY - rect.top) / rect.height) * canvas.height,
  };
}

export default function SignAgreementDialog({ bookingId, tenantName, defaultAddress = "", onSigned, trigger }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("draw"); // 'draw' | 'type'
  const [address, setAddress] = useState(defaultAddress);
  const [drawn, setDrawn] = useState("");
  const [typed, setTyped] = useState(tenantName || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!address.trim() || address.trim().length < 5) return toast.error("Enter your current full address");
    const sig = mode === "draw" ? drawn : typed.trim();
    if (!sig) return toast.error(mode === "draw" ? "Please draw your signature" : "Please type your full name");
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/sign`, {
        full_address: address.trim(),
        signature_type: mode,
        signature_data: sig,
      });
      toast.success("Agreement signed. Your PDF is now personalised and shared with the owner.");
      setOpen(false);
      onSigned?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)} className="inline-block cursor-pointer">{trigger}</div>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Sign your rental agreement</DialogTitle>
          <DialogDescription>
            Fill your current address and sign the agreement. Once signed, both you and the owner get the personalised PDF.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Your current full address</Label>
            <Textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={3}
              placeholder="House / flat number, street, area, city, state, PIN"
              data-testid="sign-address"
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#5C6B7A] mb-2">Signature</div>
            <div className="inline-flex bg-[#F0EBE1] rounded-md p-1 gap-1">
              <button
                type="button"
                onClick={() => setMode("draw")}
                className={`px-3 py-1.5 rounded text-xs uppercase tracking-[0.14em] inline-flex items-center gap-1 ${mode === "draw" ? "bg-[#FDFBF7] shadow" : "text-[#5C6B7A]"}`}
                data-testid="sign-mode-draw"
              >
                <PenLine size={12} /> Draw
              </button>
              <button
                type="button"
                onClick={() => setMode("type")}
                className={`px-3 py-1.5 rounded text-xs uppercase tracking-[0.14em] inline-flex items-center gap-1 ${mode === "type" ? "bg-[#FDFBF7] shadow" : "text-[#5C6B7A]"}`}
                data-testid="sign-mode-type"
              >
                <Type size={12} /> Type
              </button>
            </div>
            <div className="mt-2">
              {mode === "draw" ? (
                <SignaturePad onCommit={setDrawn} />
              ) : (
                <div>
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Full name as it should appear" data-testid="sign-typed" />
                  {typed && (
                    <div className="mt-3 px-3 py-4 border rounded-md bg-[#FDFBF7] font-display italic text-3xl text-[#1A1F2C]" data-testid="sign-typed-preview">
                      {typed}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="sign-submit">
            {busy && <Loader2 size={14} className="mr-1 animate-spin" />}
            Sign &amp; save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
